'use client';

import { DialogContentProps } from '@ark-ui/react';
import { maybe } from '@passionware/monads';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Slot } from '@radix-ui/react-slot';
import { X } from 'lucide-react';
import React, {
  ComponentPropsWithoutRef,
  createContext,
  ElementRef,
  forwardRef,
  HTMLAttributes,
  PropsWithChildren,
  ReactNode,
  Ref,
  useContext
} from 'react';
import { cn } from 'v5/platform/dom/cn';
import { StopPropagation } from 'v5/platform/react/StopPropagation';
import {
  ScrollShadowWrapper,
  ScrollShadowWrapperProps
} from '../primitives/ScrollShadowWrapper';
import { Button } from './Button';
import { usePortalContainer } from 'v5/primitives/PortalContainer';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-charcoal-dark/80',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    closeButton?: ReactNode;
  }
>(({ className, children, closeButton, ...props }, ref) => (
  <DialogPortal container={usePortalContainer()}>
    <DialogOverlay />
    <StopPropagation>
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'text-fg-default',
          'fixed left-[50%] top-[50%] z-50 w-full max-w-96 translate-x-[-50%] translate-y-[-50%]  border border-border-default bg-bg-default shadow-lg duration-200 sm:rounded-lg',
          'flex flex-col max-h-[calc(100vh-var(--spacing)*16)]',
          'origin-[var(--radix-dialog-content-transform-origin)]',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
          'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
          className
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute m-2 right-1 top-1.5" asChild>
          {closeButton ?? (
            <Button variant="secondary" layout="icon" size="sm">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          )}
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </StopPropagation>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const ctx = createContext<
  (DialogContentProps & { ref?: Ref<HTMLElement> }) | null
>(null);

const DialogContextProvider = forwardRef<
  HTMLElement,
  Partial<DialogContentProps>
>(function DialogContextProvider(props: Partial<DialogContentProps>, ref) {
  const { children, ...rest } = props;
  return <ctx.Provider value={{ ...rest, ref }}>{children}</ctx.Provider>;
});

/**
 * Why do we have both DialogContentHeadless and DialogContentLayout?
 *
 * This separation allows us to define the root dialog content (such as max width, layout, and structure)
 * in a component (DialogContentLayout) that does not need to be rendered all the time, but only when the dialog is open.
 * DialogContentHeadless sets up the dialog context and portal infrastructure, but does not render any layout or UI,
 * making it ideal for cases where you want to control when and how the dialog content is mounted.
 * This pattern improves performance and flexibility, as the heavy layout and UI logic is only rendered
 * when the dialog is actually open, while still allowing for custom dialog content and advanced composition.
 */

export const DialogContentHeadless = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  PropsWithChildren<ComponentPropsWithoutRef<typeof DialogPrimitive.Content>>
>(({ children, ...props }, ref) => (
  <DialogPortal container={usePortalContainer()}>
    <DialogOverlay />
    <StopPropagation>
      <DialogPrimitive.Content ref={ref} asChild {...props}>
        <DialogContextProvider>{children}</DialogContextProvider>
      </DialogPrimitive.Content>
    </StopPropagation>
  </DialogPortal>
));

export const DialogContentLayout = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  PropsWithChildren & {
    className?: string;
    closeButton?: ReactNode;
  } & HTMLAttributes<HTMLDivElement>
>(({ className, children, closeButton, ...props }, ref) => {
  const ctxVal = maybe.getOrThrow(
    useContext(ctx),
    'DialogContentLayout must be used within DialogContentHeadless or DialogContextProvider'
  );
  return (
    <Slot {...ctxVal}>
      <div
        ref={ref}
        className={cn(
          'text-fg-default',
          'fixed left-[50%] top-[50%] z-50 w-full max-w-96 translate-x-[-50%] translate-y-[-50%] border border-border-default bg-bg-default shadow-lg duration-200 sm:rounded-lg',
          'flex flex-col max-h-[calc(100vh-var(--spacing)*16)]',
          'origin-[var(--radix-dialog-content-transform-origin)]',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
          'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
          className
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute m-2 right-1 top-1.5" asChild>
          {closeButton ?? (
            <Button variant="secondary" layout="icon" size="sm">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          )}
        </DialogPrimitive.Close>
      </div>
    </Slot>
  );
});

const DialogHeader = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col gap-0 text-center sm:text-left', className)}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogBody = ({
  className,
  children,
  scrollerClassName,
  ...props
}: HTMLAttributes<HTMLDivElement> &
  ScrollShadowWrapperProps & { scrollerClassName?: string }) => {
  const content = <div className={cn('py-3 px-6', className)}>{children}</div>;
  if (props.renderTopShadow === false && props.renderBottomShadow === false) {
    return content;
  }
  return (
    <ScrollShadowWrapper {...props} className={scrollerClassName}>
      {content}
    </ScrollShadowWrapper>
  );
};
DialogBody.displayName = 'DialogBody';

const DialogFooter = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 p-1.5',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight border-b border-border-default rounded-t-lg p-6',
      'flex gap-2 items-center',
      '[&_img,svg]:size-5 [&_img,svg]:-my-2',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-fg-muted px-6 pt-3 pb-0', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription
};
